import { useEffect, useMemo, useState } from 'react';
import {
  Field,
  Image as SitecoreImage,
  ImageField,
  useSitecoreContext,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import Image from 'next/image';

import { icon_globe } from '@gn-audio/icons';
import { ComponentProps, JabraIcon, withTheme } from '@gn-audio/jss-components';
import { Distributor, getDistributors, getLocaleWithRegion } from '@gn-audio/jss-core';
import { Option } from '@gn-audio/jss-core/common/models';

import JabraDropdown from 'common/components/JabraDropdown';

import { DistributorLocatorLoader } from './DistributorLocatorLoader';

type CountryItem = {
  title: Field<string>;
  value: Field<string>;
  icon: ImageField;
  flagDescription: Field<string>;
};

type DistributorLocatorFields = {
  availableCountries: CountryItem[];
  dropdownTitle: Field<string>;
  noResultsMessage: Field<string>;
  phoneTitle: Field<string>;
  productsAvailableTitle: Field<string>;
  salesCentersTitle: Field<string>;
};

export type DistributorLocatorProps = {
  fields: DistributorLocatorFields;
} & ComponentProps;

const DistributorLocator = ({ fields }: DistributorLocatorProps) => {
  const { sitecoreContext } = useSitecoreContext();
  const {
    availableCountries,
    dropdownTitle,
    noResultsMessage,
    phoneTitle,
    productsAvailableTitle,
    salesCentersTitle,
  } = fields;

  const countriesOptions: Option[] = useMemo(() => {
    if (!availableCountries?.length) return [];

    return availableCountries
      .filter(
        (country: CountryItem) =>
          country?.title?.value?.trim() &&
          country?.value?.value?.trim() &&
          country?.icon?.value?.src
      )
      .map((country: CountryItem) => ({
        key: country.value.value,
        title: country.title.value,
        value: country.value.value,
        icon: (
          <SitecoreImage
            className="mr-3 inline-block flex-shrink-0"
            field={country.icon.value}
            alt={country.flagDescription?.value}
            width={20}
            height={20}
          />
        ),
      }));
  }, [availableCountries]);

  const [selectedCountry, setSelectedCountry] = useState<Option | null>(
    countriesOptions?.[0] || null
  );
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const localeWithRegion = getLocaleWithRegion(sitecoreContext.language);
  const region = localeWithRegion.split('-')?.[1]?.toLocaleLowerCase() || '';

  useEffect(() => {
    if (region && countriesOptions.length > 0) {
      const countryOption = countriesOptions.find(
        (option) => option.value.toLowerCase() === region
      );

      if (countryOption) {
        setSelectedCountry(countryOption);
      } else {
        setSelectedCountry(countriesOptions?.[0] || null);
      }
    }
  }, [countriesOptions, localeWithRegion, region]);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchDistributors = async () => {
      setIsLoading(true);

      try {
        const distributors = await getDistributors(selectedCountry?.value || '', abortController);
        if (!abortController.signal.aborted) {
          setDistributors(distributors);
          setIsLoading(false);
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error('Error fetching distributors:', error);
          setIsLoading(false);
        }
      }
    };

    fetchDistributors();

    return () => {
      abortController.abort();
    };
  }, [selectedCountry]);

  return (
    <div className="container">
      <div className="mb-8 flex flex-col items-center justify-between md:flex-row">
        <h2 className="mb-5 text-4xl font-bold text-black md:mb-0">{selectedCountry?.title}</h2>
        <div className="flex items-center">
          <div className="mr-2 flex items-center">
            <JabraIcon Icon={icon_globe} className="mr-2" /> {dropdownTitle?.value}
          </div>
          <div className="rounded-md border border-gray-300 py-2 pl-3 pr-2">
            <JabraDropdown
              searchInput
              options={countriesOptions}
              selectedOption={selectedCountry}
              onOptionSelect={(option) => setSelectedCountry(option)}
              containerClassNames="min-w-40 md:min-w-60"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
        {isLoading ? (
          <DistributorLocatorLoader />
        ) : distributors.length === 0 ? (
          <div className="py-8 text-gray-900">
            {noResultsMessage?.value} {selectedCountry?.title}
          </div>
        ) : (
          distributors.map((distributor) => (
            <div
              key={distributor.id}
              className="border-b border-gray-200 bg-white p-6 shadow-sm md:rounded-lg md:border"
            >
              <div className="flex flex-col items-center md:flex-row md:items-start md:space-x-4">
                <div className="mb-4 flex h-40 w-40 flex-shrink-0 items-center justify-center border md:mb-0">
                  {distributor.entityimage_url ? (
                    <Image
                      src={distributor.entityimage_url}
                      alt={`${distributor.name} logo`}
                      width="80"
                      height="80"
                      className="h-20 w-20 rounded border-gray-100 object-contain"
                    />
                  ) : (
                    <div className="flex h-20 w-20 items-center justify-center rounded border border-gray-200 bg-gray-100">
                      <span className="text-xs text-gray-400"></span>
                    </div>
                  )}
                </div>

                <div className="flex-1 text-center md:text-left">
                  <h3 className="mb-2 text-lg font-semibold text-gray-900">{distributor.name}</h3>

                  {distributor.websiteurl && (
                    <div className="mb-2">
                      <a
                        href={
                          distributor.websiteurl.startsWith('http')
                            ? distributor.websiteurl
                            : `https://${distributor.websiteurl}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="break-all text-sm text-blue-800 hover:text-blue-900"
                      >
                        {distributor.websiteurl}
                      </a>
                    </div>
                  )}

                  {distributor.telephone1 && (
                    <div className="mb-2 text-sm text-gray-700">
                      <span className="font-semibold">{phoneTitle?.value}</span>{' '}
                      {distributor.telephone1}
                    </div>
                  )}

                  {distributor.gnh_emailaddress && (
                    <div className="mb-2">
                      <a
                        href={`mailto:${distributor.gnh_emailaddress}`}
                        className="break-all text-sm text-blue-800 hover:text-blue-900"
                      >
                        {distributor.gnh_emailaddress}
                      </a>
                    </div>
                  )}

                  {distributor.gnh_salescenters && (
                    <div className="mb-2 text-sm text-gray-700">
                      <span className="font-semibold">{salesCentersTitle?.value}</span>
                      <div className="break-all text-gray-900">{distributor.gnh_salescenters}</div>
                    </div>
                  )}

                  {distributor.gnh_brand && (
                    <div className="mb-2 text-sm text-gray-700">
                      <span className="font-semibold">{productsAvailableTitle?.value}</span>
                      <div className="break-words text-gray-900">
                        {distributor.gnh_brand?.replace(/,/g, ', ')}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default withTheme()(withDatasourceCheck()<DistributorLocatorProps>(DistributorLocator));
